import React, { useMemo } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useCustom, useGetLocale } from "@refinedev/core";
import { useMarketplaceStore } from "../../../../../store/useMarketplaceStore";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { trTR, enUS } from "@mui/x-data-grid/locales";
import {
  PriceHistoryDialogProps,
  PriceHistoryItem,
  Row,
} from "./priceHistoryTypes";
import { SyncrosaleLoader } from "../../../../../components/layout/components/SyncrosaleLoader";

export const PriceHistoryDialog: React.FC<PriceHistoryDialogProps> = ({
  open,
  onClose,
  asin,
}) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.inventoryManagement.inventory.list",
  );
  const locale = useGetLocale();
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const columns: GridColDef[] = [
    { field: "price", headerName: translate("fields.price"), width: 150 },
    {
      field: "date",
      headerName: translate("fields.updateTime"),
      width: 180,
      flex: 1,
    },
  ];

  const { data, isLoading } = useCustom<{ data?: PriceHistoryItem[] }>({
    url: selectedCountry
      ? `store/${selectedCountry.id}/product/${asin}/price-history`
      : "",
    method: "get",
    queryOptions: {
      enabled: open && !!selectedCountry,
    },
  });

  const rows = useMemo(() => {
    if (data?.data && Array.isArray(data.data)) {
      const formattedRows: Row[] = data.data.map((item, index) => {
        const price =
          item.price.finalPrice && !isNaN(item.price.finalPrice)
            ? item.price.finalPrice.toFixed(2)
            : "";
        const date = item.updated
          ? new Date(item.updated).toLocaleString()
          : "";
        return {
          id: index + 1,
          price: price ? `$${price}` : "",
          date,
        };
      });
      return formattedRows;
    }
    return [];
  }, [data]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {translate("priceHistory.priceHistoryDetails", {
          asin: asin.toUpperCase(),
        })}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <SyncrosaleLoader />
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            disableColumnFilter
            localeText={
              (locale() === "tr" ? trTR : enUS).components.MuiDataGrid
                .defaultProps.localeText
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {translateWithoutPrefix("buttons.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
